import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IBursaAPIStbCert, defaultValue } from 'app/shared/model/bursa-api-stb-cert.model';

const initialState: EntityState<IBursaAPIStbCert> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/bursa-api-stb-certs';

// Actions

export const getEntities = createAsyncThunk('bursaAPIStbCert/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IBursaAPIStbCert[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'bursaAPIStbCert/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IBursaAPIStbCert>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'bursaAPIStbCert/create_entity',
  async (entity: IBursaAPIStbCert, thunkAPI) => {
    const result = await axios.post<IBursaAPIStbCert>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'bursaAPIStbCert/update_entity',
  async (entity: IBursaAPIStbCert, thunkAPI) => {
    const result = await axios.put<IBursaAPIStbCert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'bursaAPIStbCert/partial_update_entity',
  async (entity: IBursaAPIStbCert, thunkAPI) => {
    const result = await axios.patch<IBursaAPIStbCert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'bursaAPIStbCert/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IBursaAPIStbCert>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getEntityByEcertNo = createAsyncThunk(
  'bursaAPIStbCert/fetch_entity_stb_cert_by_ecert_no',
  async (ecertNo: string) => {
    const requestUrl = `${apiUrl}/by-ecert-no/${ecertNo}`;
    return axios.get<IBursaAPIStbCert>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const BursaAPIStbCertSlice = createEntitySlice({
  name: 'bursaAPIStbCert',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEntityByEcertNo.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntityByEcertNo), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = BursaAPIStbCertSlice.actions;

// Reducer
export default BursaAPIStbCertSlice.reducer;
